import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { HardDrive, Loader2, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";

interface Candidate {
  path: string;
  size: number;
  created_at: string | null;
  known_orphan: boolean;
}

function formatBytes(bytes: number) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function StorageCleanup() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastFreed, setLastFreed] = useState<number | null>(null);

  async function call(body: Record<string, unknown>) {
    const { data, error } = await supabase.functions.invoke("storage-cleanup", { body });
    if (error) {
      let detail = error.message;
      try {
        const parsed = await (error as { context?: Response }).context?.json();
        if (parsed?.error) detail = parsed.error;
      } catch {
        /* keep the original message */
      }
      throw new Error(detail);
    }
    return data as Record<string, any>;
  }

  async function handleScan() {
    setLoading(true);
    try {
      const data = await call({ action: "list" });
      const rows: Candidate[] = data.candidates ?? [];
      setCandidates(rows);
      setSelected(new Set(rows.filter((r) => r.known_orphan).map((r) => r.path)));
      setScanned(true);
      setLastFreed(null);
    } catch (e) {
      toast({
        title: "Could not scan storage",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      const data = await call({ action: "delete", paths: Array.from(selected) });
      const freed = data.freed_bytes ?? 0;
      setLastFreed(freed);
      toast({
        title:
          data.deleted_count > 0
            ? `Removed ${data.deleted_count} file${data.deleted_count === 1 ? "" : "s"}`
            : "Nothing to remove",
        description:
          data.deleted_count > 0
            ? `Freed ${formatBytes(freed)}.`
            : "Those files were already gone. Nothing changed.",
      });
      await handleScan();
    } catch (e) {
      toast({
        title: "Could not remove files",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  }

  const toggle = (path: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const selectedBytes = candidates
    .filter((c) => selected.has(c.path))
    .reduce((n, c) => n + c.size, 0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Storage cleanup</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Finds leftover constitution uploads that no member record points to, so you can
          remove them after review.
        </p>
      </div>

      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertDescription>
          Only files that no current record references are listed, and the check runs again
          at the moment you delete. Running this twice is safe: the second run finds nothing.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-4 w-4" />
              Unreferenced constitution files
            </CardTitle>
            <CardDescription>
              Older than 7 days, plus the two known leftovers from the re-upload issue.
            </CardDescription>
          </div>
          <Button onClick={handleScan} disabled={loading || deleting} variant="outline" size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {scanned ? "Scan again" : "Scan"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {!scanned && (
            <p className="text-sm text-muted-foreground">
              Run a scan to see what can be removed.
            </p>
          )}

          {scanned && candidates.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nothing to clean up.
              {lastFreed !== null && ` Freed ${formatBytes(lastFreed)} on the last run.`}
            </p>
          )}

          {candidates.length > 0 && (
            <>
              <ul className="divide-y rounded-md border">
                {candidates.map((c) => (
                  <li key={c.path} className="flex items-start gap-3 p-3">
                    <Checkbox
                      checked={selected.has(c.path)}
                      onCheckedChange={() => toggle(c.path)}
                      className="mt-0.5"
                      aria-label={`Select ${c.path}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-mono break-all">{c.path}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatBytes(c.size)}
                        {c.created_at
                          ? ` · uploaded ${new Date(c.created_at).toLocaleDateString()}`
                          : ""}
                      </p>
                    </div>
                    {c.known_orphan && (
                      <Badge variant="secondary" className="shrink-0">
                        Known leftover
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {selected.size} selected · {formatBytes(selectedBytes)}
                </p>
                <Button
                  variant="destructive"
                  disabled={selected.size === 0 || deleting || loading}
                  onClick={() => setConfirmOpen(true)}
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete selected
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} file(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This frees about {formatBytes(selectedBytes)}. Files that a member record points
              to are skipped automatically. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
